import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CLUB_LABELS = {
  "bsv-niners": "BSV Niners Wels (Pool Billard)",
  "dsv-niners": "DSV Niners Wels (E-Dart)",
  "sdv-niners": "SDV Niners Wels (Steel Dart)",
  "perchten": "D´Wösa Höllen Viecha",
};

export default function MemberRegisterPage() {
  usePageTitle("Mitglied werden");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", password2: "", gender: "", birthdate: "" });
  const [selectedClubs, setSelectedClubs] = useState([]);
  const [registerAsWebuser, setRegisterAsWebuser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef(null);

  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const toggleClub = (id) => {
    setSelectedClubs(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error("Passwörter stimmen nicht überein"); return; }
    if (form.password.length < 6) { toast.error("Passwort muss mindestens 6 Zeichen lang sein"); return; }
    if (selectedClubs.length === 0 && !registerAsWebuser) { toast.error("Bitte mindestens einen Verein oder 'Niners Friends' auswählen"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/members/register`, {
        name: form.name, email: form.email, phone: form.phone,
        password: form.password, gender: form.gender, birthdate: form.birthdate,
        clubs: selectedClubs, registered_user: registerAsWebuser
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="py-12 md:py-0 md:min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-white/10 p-8 text-center" data-testid="register-success">
          <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-xl font-bold uppercase mb-2" style={{ fontFamily: 'Oswald' }}>Registrierung erfolgreich!</h2>
          <p className="text-muted-foreground text-sm mb-6">Ihr Antrag wird vom jeweiligen Vereinsadmin geprüft. Sie werden per E-Mail benachrichtigt.</p>
          <Link to="/mitglied-login">
            <Button className="w-full rounded-none" data-testid="goto-login-btn">Zum Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-0 md:min-h-screen flex items-center justify-center p-4">
      <div ref={formRef} className="max-w-md w-full bg-card border border-white/10 p-8">
        <div className="flex items-center gap-3 mb-6">
          <UserPlus className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Mitglied werden</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm">Name *</Label>
            <Input required value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
              className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-name" placeholder="Vor- und Nachname" />
          </div>
          <div>
            <Label className="text-sm">E-Mail *</Label>
            <Input required type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
              className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-email" placeholder="max@beispiel.at" />
          </div>
          <div>
            <Label className="text-sm">Telefon *</Label>
            <Input required value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
              className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-phone" placeholder="+43 ..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Geschlecht *</Label>
              <select required value={form.gender} onChange={e => setForm(p => ({...p, gender: e.target.value}))}
                className="mt-1 w-full h-10 px-3 bg-input/50 border border-white/10 text-foreground text-sm" data-testid="register-gender">
                <option value="">Bitte wählen</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="other">Divers</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Geburtsdatum *</Label>
              <Input required type="date" value={form.birthdate} onChange={e => setForm(p => ({...p, birthdate: e.target.value}))}
                className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-birthdate" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Passwort * (min. 6 Zeichen)</Label>
            <Input required type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))}
              className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-password" />
          </div>
          <div>
            <Label className="text-sm">Passwort bestätigen *</Label>
            <Input required type="password" value={form.password2} onChange={e => setForm(p => ({...p, password2: e.target.value}))}
              className="mt-1 bg-input/50 border-white/10 rounded-none" data-testid="register-password2" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 block">Verein(e) auswählen *</Label>
            <div className="space-y-2">
              {Object.entries(CLUB_LABELS).map(([id, label]) => (
                <label key={id} className="flex items-center gap-3 p-3 border border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
                  data-testid={`register-club-${id}`}>
                  <input type="checkbox" checked={selectedClubs.includes(id)} onChange={() => toggleClub(id)}
                    className="w-4 h-4 accent-primary" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
              <label className="flex items-center gap-3 p-3 border border-blue-500/20 bg-blue-500/5 cursor-pointer hover:bg-blue-500/10 transition-colors"
                data-testid="register-webuser">
                <input type="checkbox" checked={registerAsWebuser} onChange={e => setRegisterAsWebuser(e.target.checked)}
                  className="w-4 h-4 accent-blue-500" />
                <span className="text-sm">Niners Friends</span>
              </label>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-none font-bold uppercase" data-testid="register-submit">
            {loading ? "Wird gesendet..." : "Registrieren"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Bereits registriert? <Link to="/mitglied-login" className="text-primary hover:underline">Einloggen</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
